import { useRequestLinesStore } from "entities/TravelLines";
import { createRef, useEffect, useReducer, useRef, useState } from "react";
import { REQUEST_TYPES } from "shared/config";
import { useLazyEffect } from "shared/hooks";
import { useRequestPersistedStore } from "../store/useRequestPersistedStore";
import { useRequestStore } from "../store/useRequestStore";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getRedirectRoute } from "../utils/getRedirectRoute";
import { toCardRequestLinesPayloadTransformer } from "entities/CostLines";
import { requestService } from "../api";

const loadingStateDefaultValues = {
  isSaving: false,
  isSubmitting: false,
  isAccepting: false,
  isRejecting: false,
  isReturning: false,
  isWithdrawing: false,
  isSubmitted: false,
  isAccepted: false,
};

export function useRequestActions(request) {
  const navigate = useNavigate();
  const { t } = useTranslation([
    "Request",
    "RequestBasicDetails",
    "A1",
    "Shared",
  ]);

  const [loadingState, updateLoadingState] = useReducer(
    (prev, next) => ({ ...prev, ...next }),
    loadingStateDefaultValues
  );

  const viewRequestAs = useRequestPersistedStore(
    (state) => state.viewRequestAs
  );

  const setRequest = useRequestStore((state) => state.setRequest);
  const setRequestBasicDetailsToUpdate = useRequestStore(
    (state) => state.setRequestBasicDetailsToUpdate
  );
  const requestBasicDetailsToUpdate = useRequestStore(
    (state) => state.requestBasicDetailsToUpdate
  );
  const updateBasicDetails = useRequestStore(
    (state) => state.updateBasicDetails
  );
  const resetRequestLinesStack = useRequestLinesStore(
    (state) => state.resetRequestLinesStack
  );
  const requestLinesStack = useRequestLinesStore(
    (state) => state.requestLinesStack
  );

  // TODO support for different request lines types...
  const [requestLinesRefs, setRequestLinesRefs] = useState([]);

  useEffect(() => {
    setRequestLinesRefs((ref) =>
      Array(request?.credit_cards_costs_transactions?.data?.length)
        .fill()
        .map((_, i) => ref[i] || createRef())
    );
  }, [request]);

  const requestBasicDetailsRef = useRef(null);

  const _handleRequestBasicDetailsUpdate = async (dataToUpdatePromise) => {
    if (dataToUpdatePromise) {
      const { isDirty, isValid, ...dataToUpdate } = await dataToUpdatePromise;

      if (!isValid)
        throw new Error("Validation errors in request basic details.");

      if (!isDirty) return;

      await requestService
        .updateRequest(
          request?.number,
          {
            user_role: viewRequestAs.role,
            ...dataToUpdate.formData,
          },
          {
            onSuccess: t("toasts.request_was_updated", { ns: "Request" }),
          }
        )
        .then((response) => {
          updateBasicDetails(response);
        });
    }
  };

  const _handleRequestLinesSave = async (linesToSavePromise) => {
    if (!linesToSavePromise.length) {
      return;
    }

    const linesToSave = await Promise.all(linesToSavePromise);

    const { validLines, invalidLines } = linesToSave.reduce(
      (acc, line) => {
        line.isValid
          ? acc.validLines.push(line.formData)
          : acc.invalidLines.push(line.errors);
        return acc;
      },
      { validLines: [], invalidLines: [] }
    );

    if (invalidLines.length)
      throw new Error("Validation errors in request lines.");

    const modifedLines = validLines.filter((line) => line?.isDirty);

    modifedLines.forEach((line) => {
      delete line.isDirty;
    });

    const { eurocardRequestLines, creditcardRequestLines } =
      modifedLines.reduce(
        (acc, { lineType, ...rest }) => {
          if (lineType === REQUEST_TYPES.EXPENSE)
            acc.expenseRequestLines.push(rest);
          if (lineType === REQUEST_TYPES.TRAVEL)
            acc.travelRequestLines.push(rest);
          if (lineType === REQUEST_TYPES.EUROCARD)
            acc.eurocardRequestLines.push(rest);
          if (
            lineType === REQUEST_TYPES.AMEX_BTA ||
            lineType === REQUEST_TYPES.CREDIT_CARD
          )
            acc.creditcardRequestLines.push(rest);
          return acc;
        },
        {
          expenseRequestLines: [],
          travelRequestLines: [],
          creditcardRequestLines: [],
          eurocardRequestLines: [],
        }
      );

    if (creditcardRequestLines.length > 0 || eurocardRequestLines.length > 0) {
      const cardRequestLinesPayload = toCardRequestLinesPayloadTransformer(
        creditcardRequestLines.length > 0
          ? creditcardRequestLines
          : eurocardRequestLines
      );

      await requestService
        .updateCardRequest({
          application_number: request.number,
          user_role: viewRequestAs.role,
          ...cardRequestLinesPayload,
        })
        .then((requestData) => setRequest(requestData));
    }

    // TODO other types of lines...
  };

  useLazyEffect(
    () =>
      Promise.all([
        _handleRequestLinesSave(requestLinesStack),
        _handleRequestBasicDetailsUpdate(requestBasicDetailsToUpdate),
      ])
        .then(async () => {
          if (loadingState.isSubmitting) {
            if (
              [
                REQUEST_TYPES.CREDIT_CARD,
                REQUEST_TYPES.AMEX_BTA,
                REQUEST_TYPES.EUROCARD,
              ].includes(request.type)
            ) {
              await requestService
                .submitCardRequest(request.type, request.id, viewRequestAs.role)
                .then(() => {
                  // @ts-ignore
                  updateLoadingState({ isSubmitted: true });
                  // Timeout prevents from second submit
                  setTimeout(() => {
                    navigate(
                      getRedirectRoute(viewRequestAs.role, request.type)
                    );
                  }, 500);
                });
            }
            // TODO submit of other request types...
          }
          if (loadingState.isReturning) {
            // TODO
          }
          if (loadingState.isAccepting) {
            await requestService
              .acceptRequest(viewRequestAs.role, request?.id)
              .then(
                async () =>
                  await requestService
                    .submitRequest(viewRequestAs.role, request?.id, {
                      onSuccess: t("toasts.request_was_accepted", {
                        ns: "Request",
                      }),
                    })
                    .then(() => {
                      // @ts-ignore
                      updateLoadingState({ isAccepted: true });
                      // Timeout prevents from second submit
                      setTimeout(() => {
                        navigate(
                          getRedirectRoute(
                            viewRequestAs.role,
                            request?.type?.value
                          )
                        );
                      }, 500);
                    })
              );
          }
        })
        .catch(console.error)
        .finally(() => {
          if (requestBasicDetailsToUpdate) setRequestBasicDetailsToUpdate(null);
          if (requestLinesStack.length) resetRequestLinesStack();
          if (Object.values(loadingState).some(Boolean)) {
            // @ts-ignore
            updateLoadingState(loadingStateDefaultValues);
          }
        }),
    [
      requestLinesStack,
      requestBasicDetailsToUpdate,
      loadingState.isSubmitting,
      loadingState.isAccepting,
      loadingState.isReturning,
    ],
    100
  );

  const triggerRequestReject = async (reasonOfRejection) => {
    // @ts-ignore
    updateLoadingState({ isRejecting: true });
    await requestService
      .rejectRequest(viewRequestAs.role, request?.id, reasonOfRejection)
      .then(async () =>
        requestService
          .submitRequest(viewRequestAs.role, request?.id)
          .then(() => {
            navigate(getRedirectRoute(viewRequestAs.role, request?.type));
          })
      )
      .finally(() => {
        // @ts-ignore
        updateLoadingState({ isRejecting: false });
      });
  };

  const triggerRequestSave = () => {
    // @ts-ignore
    updateLoadingState({ isSaving: true });
    requestBasicDetailsRef.current?.submit();
    requestLinesRefs.forEach((ref) => ref.current.save());
  };

  const triggerRequestSubmit = () => {
    // @ts-ignore
    updateLoadingState({ isSubmitting: true });
    requestBasicDetailsRef.current?.submit();
    requestLinesRefs.forEach((ref) => ref.current.submit());
  };
  const triggerRequestAccept = () => {
    // @ts-ignore
    updateLoadingState({ isAccepting: true });
  };

  return {
    triggerRequestSave,
    triggerRequestSubmit,
    triggerRequestReject,
    triggerRequestAccept,
    requestLinesRefs,
    requestBasicDetailsRef,
    loadingState,
  };
}
